// src/components/approach/ApproachJourney.js

import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Search, Target, Zap, Layers, Rocket, HeartHandshake,
  ArrowRight
} from 'lucide-react';
import './ApproachJourney.css';

// Register GSAP plugin
gsap.registerPlugin(ScrollTrigger);

// Breakpoint for switching to mobile layout (phones only, tablets get horizontal scroll)
const MOBILE_BREAKPOINT = 768;

const steps = [
  {
    id: 1,
    number: "01",
    title: "Deep Discovery",
    subtitle: "Understand your operational reality",
    description: "Map workflows, interview stakeholders, document pain points, and align on success criteria grounded in how your business actually runs.",
    deliverables: ["Process maps", "Stakeholder interviews", "Pain point analysis"],
    icon: Search,
    accent: "#00D9FF"
  },
  {
    id: 2,
    number: "02",
    title: "Opportunity Mapping",
    subtitle: "Prioritize high-value use cases",
    description: "Translate findings into a prioritized list of AI opportunities tied directly to measurable business outcomes.",
    deliverables: ["Use case prioritization", "ROI models", "Implementation roadmap"],
    icon: Target,
    accent: "#8B5CF6"
  },
  {
    id: 3,
    number: "03",
    title: "Rapid Prototyping",
    subtitle: "Build fast, validate early",
    description: "Stand up working prototypes quickly so stakeholders can see and touch the solution, not just read about it.",
    deliverables: ["Functional prototypes", "Demo environments", "User testing"],
    icon: Zap,
    accent: "#FF6B9D"
  },
  {
    id: 4,
    number: "04",
    title: "Production Build",
    subtitle: "Engineer enterprise-grade solutions",
    description: "Develop production-ready AI systems with robust architecture, comprehensive testing, security compliance, and scalability built-in from day one.",
    deliverables: ["Production system", "API documentation", "Security audit"],
    icon: Layers,
    accent: "#10B981"
  },
  {
    id: 5,
    number: "05",
    title: "Deployment & Integration",
    subtitle: "Seamlessly integrate into operations",
    description: "Deploy AI solutions into your existing infrastructure with minimal disruption. Provide comprehensive training, documentation, and change management support.",
    deliverables: ["Deployed system", "Training programs", "Integration documentation"],
    icon: Rocket,
    accent: "#F59E0B"
  },
  {
    id: 6,
    number: "06",
    title: "Scale & Optimize",
    subtitle: "Continuous improvement and growth",
    description: "Monitor performance, gather feedback, optimize models, and scale successful solutions across your organization while maintaining peak performance.",
    deliverables: ["Performance dashboard", "Optimization reports", "Scaling roadmap"],
    icon: HeartHandshake,
    accent: "#EC4899"
  },
  {
    id: 7,
    number: "07",
    title: "Begin Your Transformation",
    subtitle: "Your journey starts now",
    description: "You've seen our proven methodology. Now it's time to take the first step toward transforming your business with AI.",
    icon: ArrowRight,
    accent: "#00D9FF",
    isCTA: true
  }
];

const ApproachJourney = () => {
  const sectionRef = useRef(null);
  const triggerRef = useRef(null);
  const cardsContainerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  // Detect mobile/tablet on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // GSAP horizontal scroll - only on desktop
  useLayoutEffect(() => {
    if (isMobile || !triggerRef.current || !cardsContainerRef.current) return;

    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray('.journey-card');
      const totalCards = cards.length;
      const cardWidth = 280;
      const cardGap = 24;

      const viewportWidth = window.innerWidth;
      const centerOffset = (viewportWidth - cardWidth) / 2;
      const totalScrollDistance = (totalCards - 1) * (cardWidth + cardGap);

      gsap.set(cardsContainerRef.current, {
        x: centerOffset
      });

      gsap.to(cardsContainerRef.current, {
        x: centerOffset - totalScrollDistance,
        ease: "none",
        scrollTrigger: {
          trigger: triggerRef.current,
          pin: true,
          scrub: 0.8,
          start: "top 290px",
          end: () => `+=${totalScrollDistance * 1.5}`,
          invalidateOnRefresh: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            const progress = self.progress;
            const newIndex = Math.min(
              Math.round(progress * (totalCards - 1)),
              totalCards - 1
            );

            setActiveIndex(newIndex);

            // Hide scroll hint after user starts scrolling
            if (progress > 0.05 && !hasScrolled) {
              setHasScrolled(true);
            }

            cards.forEach((card, index) => {
              if (index === newIndex) {
                card.classList.add('is-active');
              } else {
                card.classList.remove('is-active');
              }
            });
          }
        }
      });

    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile]);

  const currentStep = steps[activeIndex] || steps[0];

  return (
    <section ref={sectionRef} className={`approach-journey ${isMobile ? 'is-mobile' : ''}`} id="approach">
      {/* Background Elements */}
      <div className="journey-bg">
        <div className="journey-bg__gradient" />
        <div className="journey-bg__grid" />
      </div>

      {/* Sticky Header */}
      <div className="journey-sticky-header">
        <div className="journey-sticky-content">
          <motion.div
            className="journey-sticky-inner"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="journey-sticky-badge">
              <span className="journey-sticky-badge-icon">⬡</span>
              Our Methodology
            </div>

            <h2 className="journey-sticky-title">
              The Journey to
              <span className="journey-sticky-title-gradient"> Transformation</span>
            </h2>

            <p className="journey-sticky-subtitle">
              A proven approach that moves quickly from understanding to impact
            </p>

            {/* Progress Indicator - Shows on both desktop and mobile */}
            <div className="journey-sticky-progress">
              <div className="journey-sticky-progress-info">
                <span className="journey-sticky-progress-current">
                  Step {String(activeIndex + 1).padStart(2, '0')}
                </span>
                <span className="journey-sticky-progress-separator">/</span>
                <span className="journey-sticky-progress-total">07</span>
                <span className="journey-sticky-progress-divider">•</span>
                <span
                  className="journey-sticky-progress-title"
                  style={{ color: currentStep.isCTA ? '#00D9FF' : currentStep.accent }}
                >
                  {currentStep.title}
                </span>
              </div>

              <div className="journey-sticky-progress-bar-container">
                <div className="journey-sticky-progress-bar">
                  <div
                    className="journey-sticky-progress-fill"
                    style={{
                      width: `${((activeIndex + 1) / steps.length) * 100}%`,
                      background: `linear-gradient(90deg, ${currentStep.accent}, ${steps[Math.min(activeIndex + 1, steps.length - 1)].accent})`
                    }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Pinned Scrolling Container */}
      <div ref={triggerRef} className="journey-trigger">
        {/* Mobile: Simple Vertical Cards */}
        {isMobile ? (
          <div className="journey-viewport journey-viewport--mobile">
            <div className="journey-cards journey-cards--mobile">
              {steps.map((step, index) => (
                <JourneyCard
                  key={step.id}
                  step={step}
                  index={index}
                  isActive={index === activeIndex}
                />
              ))}
            </div>
          </div>
        ) : (
          /* Desktop: Horizontal Scrolling Cards */
          <div className="journey-viewport">
            <div ref={cardsContainerRef} className="journey-cards">
              {steps.map((step, index) => (
                <JourneyCard
                  key={step.id}
                  step={step}
                  index={index}
                  isActive={index === activeIndex}
                />
              ))}
            </div>

            {/* Animated Progress Line */}
            <div className="journey-line">
              <svg className="journey-line__svg" preserveAspectRatio="none">
                <defs>
                  {/* Background gradient - muted */}
                  <linearGradient id="lineGradientBg" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(0, 217, 255, 0.15)" />
                    <stop offset="100%" stopColor="rgba(236, 72, 153, 0.15)" />
                  </linearGradient>
                  {/* Active gradient - vibrant */}
                  <linearGradient id="lineGradientActive" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#00D9FF" />
                    <stop offset="16%" stopColor="#8B5CF6" />
                    <stop offset="33%" stopColor="#FF6B9D" />
                    <stop offset="50%" stopColor="#10B981" />
                    <stop offset="66%" stopColor="#F59E0B" />
                    <stop offset="83%" stopColor="#EC4899" />
                    <stop offset="100%" stopColor="#00D9FF" />
                  </linearGradient>
                  {/* Glow filter */}
                  <filter id="lineGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                {/* Background line */}
                <line
                  x1="0"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                  stroke="url(#lineGradientBg)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                {/* Active progress line */}
                <line
                  className="journey-line__progress"
                  x1="0"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                  stroke="url(#lineGradientActive)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  filter="url(#lineGlow)"
                  style={{
                    strokeDasharray: '100%',
                    strokeDashoffset: `${100 - ((activeIndex + 1) / steps.length) * 100}%`,
                    transition: 'stroke-dashoffset 0.5s cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                />
                {/* Animated particle/glow dot */}
                <circle
                  className="journey-line__particle"
                  r="5"
                  fill={currentStep.accent || '#00D9FF'}
                  filter="url(#lineGlow)"
                  style={{
                    cx: `${((activeIndex + 1) / steps.length) * 100}%`,
                    cy: '50%',
                    transition: 'cx 0.5s cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                />
              </svg>
            </div>

            {/* Scroll Hint - Desktop only, fades after scrolling starts */}
            <motion.div
              className={`journey-hint ${hasScrolled ? 'is-hidden' : ''}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: hasScrolled ? 0 : 1, y: hasScrolled ? 20 : 0 }}
              transition={{ duration: 0.4, delay: hasScrolled ? 0 : 1 }}
            >
              <span>Scroll to explore</span>
              <motion.div
                className="journey-hint__arrow"
                animate={{ x: [0, 8, 0] }}
                transition={{ duration: 1.2, repeat: Infinity }}
              >
                →
              </motion.div>
            </motion.div>
          </div>
        )}
      </div>

    </section>
  );
};

// Individual Journey Card Component
const JourneyCard = ({ step, index, isActive }) => {
  const Icon = step.icon;

  // CTA Card - Special variant for the final step
  if (step.isCTA) {
    return (
      <div
        className={`journey-card journey-card--cta ${isActive ? 'is-active' : ''}`}
        style={{ '--card-accent': step.accent }}
      >
        {/* Connection Node */}
        <div className="journey-card__node">
          <div className="journey-card__node-pulse" />
          <div className="journey-card__node-dot" />
        </div>

        {/* CTA Card Content */}
        <div className="journey-card__inner journey-card__inner--cta">
          {/* Header */}
          <div className="journey-card__header">
            <div className="journey-card__icon journey-card__icon--cta">
              <Icon size={24} strokeWidth={2} />
            </div>
            <span className="journey-card__number">{step.number}</span>
          </div>

          {/* Title */}
          <h3 className="journey-card__title journey-card__title--cta">{step.title}</h3>

          {/* Subtitle */}
          <p className="journey-card__subtitle">{step.subtitle}</p>

          {/* Description */}
          <p className="journey-card__desc">{step.description}</p>

          {/* CTA Button */}
          <motion.a
            href="#contact"
            className="journey-card__cta-btn"
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            Start Your AI Journey
            <ArrowRight size={18} />
          </motion.a>
        </div>

        {/* Animated Glow Effect */}
        <div className="journey-card__glow journey-card__glow--cta" />
      </div>
    );
  }

  // Regular Card
  return (
    <div
      className={`journey-card ${isActive ? 'is-active' : ''}`}
      style={{ '--card-accent': step.accent }}
    >
      {/* Connection Node */}
      <div className="journey-card__node">
        <div className="journey-card__node-pulse" />
        <div className="journey-card__node-dot" />
      </div>

      {/* Card Content */}
      <div className="journey-card__inner">
        {/* Header */}
        <div className="journey-card__header">
          <div className="journey-card__icon">
            <Icon size={22} strokeWidth={1.5} />
          </div>
          <span className="journey-card__number">{step.number}</span>
        </div>

        {/* Title */}
        <h3 className="journey-card__title">{step.title}</h3>

        {/* Subtitle */}
        <p className="journey-card__subtitle">{step.subtitle}</p>

        {/* Description */}
        <p className="journey-card__desc">{step.description}</p>

        {/* Deliverables */}
        {step.deliverables && (
          <div className="journey-card__deliverables">
            <span className="journey-card__deliverables-label">Deliverables</span>
            <ul className="journey-card__deliverables-list">
              {step.deliverables.map((item, i) => (
                <li key={i}>
                  <span className="journey-card__deliverable-dot" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Hover Glow Effect */}
      <div className="journey-card__glow" />
    </div>
  );
};

export default ApproachJourney;
